import {
  AddRoleMutation,
  AddRoleMutationVariables,
  AddUserAndDefaultRoleMutation,
  AddUserAndDefaultRoleMutationVariables,
  AddUserAndDefaultRoleWithRoleAndPermissionsMutation,
  AddUserAndDefaultRoleWithRoleAndPermissionsMutationVariables,
  CacheBehaviour,
  ChangeRoleNameMutation,
  ChangeRoleNameMutationVariables,
  DeleteRoleAndPermissionsMutation,
  DeleteRoleAndPermissionsMutationVariables,
  GetMultiDomainAuthInfoQuery,
  GrantAssumeRoleMutation,
  GrantAssumeRoleMutationVariables,
  GrantDeleteClientFileMutation,
  GrantDeleteClientFileMutationVariables,
  GrantDownloadClientFileMutation,
  GrantDownloadClientFileMutationVariables,
  GrantExportReportsMutation,
  GrantExportReportsMutationVariables,
  GrantLoginMutation,
  GrantLoginMutationVariables,
  GrantReadClientFileMutation,
  GrantReadClientFileMutationVariables,
  GrantReadDataMutation,
  GrantReadDataMutationVariables,
  GrantUseFiltersMutation,
  GrantUseFiltersMutationVariables,
  GrantViewDashboardsMutation,
  GrantViewDashboardsMutationVariables,
  GrantViewMetricsMutation,
  GrantViewMetricsMutationVariables,
  GrantWriteClientFileMutation,
  GrantWriteClientFileMutationVariables,
  ListExecutorRolesForRoleQuery,
  ListExecutorRolesForRoleQueryVariables,
  ListUserRolesWithLoginPermissionsQuery,
  MetaDataByUserRoleQuery,
  ReplaceDeleteClientFileMutation,
  ReplaceDeleteClientFileMutationVariables,
  ReplaceDownloadClientFileMutation,
  ReplaceDownloadClientFileMutationVariables,
  ReplaceLoginMutation,
  ReplaceLoginMutationVariables,
  ReplaceReadClientFileMutation,
  ReplaceReadClientFileMutationVariables,
  ReplaceReadDataMutation,
  ReplaceReadDataMutationVariables,
  ReplaceUseFiltersMutation,
  ReplaceUseFiltersMutationVariables,
  ReplaceViewDashboardsMutation,
  ReplaceViewDashboardsMutationVariables,
  ReplaceViewMetricsMutation,
  ReplaceViewMetricsMutationVariables,
  ReplaceWriteClientFileMutation,
  ReplaceWriteClientFileMutationVariables,
  RevokeAssignedPermissionMutation,
} from '../graphql/generated/graphql-sdk';
import {
  AssignedPermission,
  AssignedPermissions,
  Permission,
  Role,
  RoleId,
} from '../permissions/permissions';
import { HasDependencies, hashString } from '../v2/api/types';
import { Domains } from '../v2/constants';
import { GraphQlRequestService } from './graphql-request-service';
import { getGraphqlResult } from './utils';

export interface PermissionService extends HasDependencies {
  listUserRoles(domain: string): Promise<Role[]>;
  listUserRolesWithLoginPermissions(
    domain: Domains
  ): Promise<
    NonNullable<ListUserRolesWithLoginPermissionsQuery['listUserRoles']>
  >;
  listNonUserRoles(domain: string): Promise<Role[] | null>;
  getMultiDomainAuthInfo(): Promise<GetMultiDomainAuthInfoQuery>;
  getAllPermissionsForRole(
    roleId: RoleId
  ): Promise<AssignedPermission<Permission>[]>;
  getDirectPermissionsForRole(
    roleId: string
  ): Promise<AssignedPermission<Permission>[]>;
  getDirectPermissionsForRoles(
    roleIds: string[]
  ): Promise<AssignedPermissions<Permission>[]>;
  revokeAssignedPermission(
    permissionId: string
  ): Promise<RevokeAssignedPermissionMutation>;
  grantAssumeRole(
    variables: Omit<GrantAssumeRoleMutationVariables, 'simulateRole'>
  ): Promise<GrantAssumeRoleMutation>;
  grantLogin(
    variables: Omit<GrantLoginMutationVariables, 'simulateRole'>
  ): Promise<GrantLoginMutation>;
  grantReadData(
    variables: Omit<GrantReadDataMutationVariables, 'simulateRole'>
  ): Promise<GrantReadDataMutation>;
  grantViewDashboards(
    variables: Omit<GrantViewDashboardsMutationVariables, 'simulateRole'>
  ): Promise<GrantViewDashboardsMutation>;
  grantExportReports(
    variables: Omit<GrantExportReportsMutationVariables, 'simulateRole'>
  ): Promise<GrantExportReportsMutation>;
  grantReadClientFile(
    variables: Omit<GrantReadClientFileMutationVariables, 'simulateRole'>
  ): Promise<GrantReadClientFileMutation>;
  grantWriteClientFile(
    variables: Omit<GrantWriteClientFileMutationVariables, 'simulateRole'>
  ): Promise<GrantWriteClientFileMutation>;
  grantDownloadClientFile(
    variables: Omit<GrantDownloadClientFileMutationVariables, 'simulateRole'>
  ): Promise<GrantDownloadClientFileMutation>;
  grantDeleteClientFile(
    variables: Omit<GrantDeleteClientFileMutationVariables, 'simulateRole'>
  ): Promise<GrantDeleteClientFileMutation>;
  grantUseFilters(
    variables: Omit<GrantUseFiltersMutationVariables, 'simulateRole'>
  ): Promise<GrantUseFiltersMutation>;
  grantViewMetrics(
    variables: Omit<GrantViewMetricsMutationVariables, 'simulateRole'>
  ): Promise<GrantViewMetricsMutation>;
  replaceReadData(
    variables: Omit<ReplaceReadDataMutationVariables, 'simulateRole'>
  ): Promise<ReplaceReadDataMutation>;
  replaceLogin(
    variables: Omit<ReplaceLoginMutationVariables, 'simulateRole'>
  ): Promise<ReplaceLoginMutation>;
  replaceViewDashboards(
    variables: Omit<ReplaceViewDashboardsMutationVariables, 'simulateRole'>
  ): Promise<ReplaceViewDashboardsMutation>;
  replaceUseFilters(
    variables: Omit<ReplaceUseFiltersMutationVariables, 'simulateRole'>
  ): Promise<ReplaceUseFiltersMutation>;
  replaceViewMetrics(
    variables: Omit<ReplaceViewMetricsMutationVariables, 'simulateRole'>
  ): Promise<ReplaceViewMetricsMutation>;
  replaceReadClientFile(
    variables: Omit<ReplaceReadClientFileMutationVariables, 'simulateRole'>
  ): Promise<ReplaceReadClientFileMutation>;
  replaceWriteClientFile(
    variables: Omit<ReplaceWriteClientFileMutationVariables, 'simulateRole'>
  ): Promise<ReplaceWriteClientFileMutation>;
  replaceDownloadClientFile(
    variables: Omit<ReplaceDownloadClientFileMutationVariables, 'simulateRole'>
  ): Promise<ReplaceDownloadClientFileMutation>;
  replaceDeleteClientFile(
    variables: Omit<ReplaceDeleteClientFileMutationVariables, 'simulateRole'>
  ): Promise<ReplaceDeleteClientFileMutation>;
  addRole(
    variables: Omit<AddRoleMutationVariables, 'simulateRole'>
  ): Promise<AddRoleMutation>;
  addUserAndDefaultRole(
    variables: Omit<AddUserAndDefaultRoleMutationVariables, 'simulateRole'>
  ): Promise<AddUserAndDefaultRoleMutation>;
  addUserAndDefaultRoleWithRoleAndPermissions(
    variables: Omit<
      AddUserAndDefaultRoleWithRoleAndPermissionsMutationVariables,
      'simulateRole'
    >
  ): Promise<AddUserAndDefaultRoleWithRoleAndPermissionsMutation>;
  deleteRoleAndPermissions(
    variables: Omit<DeleteRoleAndPermissionsMutationVariables, 'simulateRole'>
  ): Promise<DeleteRoleAndPermissionsMutation>;
  changeRoleName(
    variables: Omit<ChangeRoleNameMutationVariables, 'simulateRole'>
  ): Promise<ChangeRoleNameMutation>;
  listExecutorRolesForRole(
    variables: Omit<ListExecutorRolesForRoleQueryVariables, 'simulateRole'>,
    domain: string
  ): Promise<ListExecutorRolesForRoleQuery>;
  metaDataByUserRole(roleIds: string[]): Promise<MetaDataByUserRoleQuery>;
}

export class DefaultPermissionService implements PermissionService {
  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly simulateRoleId: RoleId | null
  ) {}

  public getDeps = () => [
    hashString([
      ...this.graphQlRequestService.getDeps(),
      this.simulateRoleId ?? undefined,
    ]),
  ];

  public listAllRoles = async () => {
    return getGraphqlResult(
      (
        await this.graphQlRequestService.graphQlSdk.listAllRoles({
          simulateRole: this.simulateRoleId,
        })
      ).listAllRoles
    );
  };

  public listUserRoles = async (domain: string) => {
    return getGraphqlResult(
      (
        await this.graphQlRequestService.graphQlSdk.listUserRoles({
          domainFilter: [domain],
          simulateRole: this.simulateRoleId,
        })
      ).listUserRoles
    );
  };

  public listUserRolesWithLoginPermissions = async (domain: Domains) => {
    return getGraphqlResult(
      (
        await this.graphQlRequestService.graphQlSdk.listUserRolesWithLoginPermissions(
          {
            domainFilter: [domain],
            simulateRole: this.simulateRoleId,
          }
        )
      ).listUserRoles
    );
  };

  public listNonUserRoles = async (domain: string) => {
    return getGraphqlResult(
      (
        await this.graphQlRequestService.graphQlSdk.listNonUserRoles({
          domain: [domain],
          simulateRole: this.simulateRoleId,
        })
      ).listNonUserRoles
    );
  };

  public getAllPermissionsForRole = async (roleId: string) => {
    return getGraphqlResult(
      (
        await this.graphQlRequestService.graphQlSdk.allPermissions({
          roleId,
          simulateRole: this.simulateRoleId,
        })
      ).allPermissions
    );
  };

  public getDirectPermissionsForRole = async (roleId: string) => {
    return getGraphqlResult(
      (
        await this.graphQlRequestService.graphQlSdk.directPermissions({
          roleId: roleId,
          simulateRole: this.simulateRoleId,
          cacheBehaviour: CacheBehaviour.SkipCacheBehaviour,
        })
      ).directPermissions
    );
  };

  public getDirectPermissionsForRoles = async (roleIds: string[]) => {
    return getGraphqlResult(
      (
        await this.graphQlRequestService.graphQlSdk.directPermissionsForRoles({
          roleIds: roleIds,
          simulateRole: this.simulateRoleId,
        })
      ).directPermissionsForRoles
    ).map((entry) => new AssignedPermissions<Permission>(entry.permissions));
  };

  public revokeAssignedPermission = async (permissionId: string) => {
    return await this.graphQlRequestService.graphQlSdk.revokeAssignedPermission(
      {
        id: permissionId,
        simulateRole: this.simulateRoleId,
      }
    );
  };

  public grantAssumeRole = async ({
    roleId,
    domain,
    roles,
    excludedRoles = [],
  }: Omit<GrantAssumeRoleMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantAssumeRole({
      roleId,
      domain,
      roles,
      excludedRoles,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantLogin = async ({
    roleId,
    domain,
  }: Omit<GrantLoginMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantLogin({
      roleId,
      domain,
      allowedAuthenticationMethods: null,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantReadData = async ({
    roleId,
    domain,
    access,
    lineHierarchyRestrictions,
    popFilter,
  }: Omit<GrantReadDataMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantReadData({
      roleId,
      domain,
      access,
      lineHierarchyRestrictions,
      popFilter,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantViewDashboards = async ({
    roleId,
    domain,
    dashboardPages,
    exportChartView,
    exportDataView,
    viewDataView,
  }: Omit<GrantViewDashboardsMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantViewDashboards({
      roleId,
      domain,
      dashboardPages,
      exportChartView,
      exportDataView,
      viewDataView,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantExportReports = async ({
    roleId,
    domain,
  }: Omit<GrantExportReportsMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantExportReports({
      roleId,
      domain,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantReadClientFile = async ({
    roleId,
    domain,
    allowedPatterns,
  }: Omit<GrantReadClientFileMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantReadClientFile({
      roleId,
      domain,
      allowedPatterns,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantWriteClientFile = async ({
    roleId,
    domain,
    allowedPatterns,
  }: Omit<GrantWriteClientFileMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantWriteClientFile({
      roleId,
      domain,
      allowedPatterns,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantDownloadClientFile = async ({
    roleId,
    domain,
    allowedPatterns,
  }: Omit<GrantDownloadClientFileMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantDownloadClientFile({
      roleId,
      domain,
      allowedPatterns,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantDeleteClientFile = async ({
    roleId,
    domain,
    allowedPatterns,
  }: Omit<GrantDeleteClientFileMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantDeleteClientFile({
      roleId,
      domain,
      allowedPatterns,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantUseFilters = async ({
    roleId,
    domain,
    allowedTags,
  }: Omit<GrantUseFiltersMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantUseFilters({
      roleId,
      domain,
      allowedTags,
      simulateRole: this.simulateRoleId,
    });
  };

  public grantViewMetrics = async ({
    roleId,
    allowedTags,
    domain,
  }: Omit<GrantViewMetricsMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.grantViewMetrics({
      domain,
      roleId,
      allowedTags,
      simulateRole: this.simulateRoleId,
    });
  };

  public replaceLogin = async (
    replaceLoginVariables: Omit<ReplaceLoginMutationVariables, 'simulateRole'>
  ) => {
    return await this.graphQlRequestService.graphQlSdk.replaceLogin({
      ...replaceLoginVariables,
      simulateRole: this.simulateRoleId,
    });
  };

  public replaceReadData = async (
    replaceReadDataVariables: Omit<
      ReplaceReadDataMutationVariables,
      'simulateRole'
    >
  ) => {
    return await this.graphQlRequestService.graphQlSdk.replaceReadData({
      ...replaceReadDataVariables,
      simulateRole: this.simulateRoleId,
    });
  };

  public replaceViewDashboards = async (
    replaceViewDashboardVariables: Omit<
      ReplaceViewDashboardsMutationVariables,
      'simulateRole'
    >
  ) => {
    return await this.graphQlRequestService.graphQlSdk.replaceViewDashboards({
      ...replaceViewDashboardVariables,
      simulateRole: this.simulateRoleId,
    });
  };

  public replaceUseFilters = async (
    replaceUseFiltersVariables: Omit<
      ReplaceUseFiltersMutationVariables,
      'simulateRole'
    >
  ) => {
    return await this.graphQlRequestService.graphQlSdk.replaceUseFilters({
      ...replaceUseFiltersVariables,
      simulateRole: this.simulateRoleId,
    });
  };

  public replaceViewMetrics = async (
    replaceViewMetricsVariables: Omit<
      ReplaceViewMetricsMutationVariables,
      'simulateRole'
    >
  ) => {
    return await this.graphQlRequestService.graphQlSdk.replaceViewMetrics({
      ...replaceViewMetricsVariables,
      simulateRole: this.simulateRoleId,
    });
  };

  public replaceReadClientFile = async (
    replaceReadClientFileVariables: Omit<
      ReplaceReadClientFileMutationVariables,
      'simulateRole'
    >
  ) => {
    return await this.graphQlRequestService.graphQlSdk.replaceReadClientFile({
      ...replaceReadClientFileVariables,
      simulateRole: this.simulateRoleId,
    });
  };

  public replaceWriteClientFile = async (
    replaceWriteClientFileVariables: Omit<
      ReplaceWriteClientFileMutationVariables,
      'simulateRole'
    >
  ) => {
    return await this.graphQlRequestService.graphQlSdk.replaceWriteClientFile({
      ...replaceWriteClientFileVariables,
      simulateRole: this.simulateRoleId,
    });
  };

  public replaceDownloadClientFile = async (
    replaceDownloadClientFileVariables: Omit<
      ReplaceDownloadClientFileMutationVariables,
      'simulateRole'
    >
  ) => {
    return await this.graphQlRequestService.graphQlSdk.replaceDownloadClientFile(
      {
        ...replaceDownloadClientFileVariables,
        simulateRole: this.simulateRoleId,
      }
    );
  };

  public replaceDeleteClientFile = async (
    replaceDeleteClientFileVariables: Omit<
      ReplaceDeleteClientFileMutationVariables,
      'simulateRole'
    >
  ) => {
    return await this.graphQlRequestService.graphQlSdk.replaceDeleteClientFile({
      ...replaceDeleteClientFileVariables,
      simulateRole: this.simulateRoleId,
    });
  };

  public addRole = async ({
    name,
    domain,
  }: Omit<AddRoleMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.addRole({
      name,
      domain,
      simulateRole: this.simulateRoleId,
    });
  };

  public addUserAndDefaultRole = async ({
    email,
    roleDomain,
    addDefaultLoginPermission,
  }: Omit<AddUserAndDefaultRoleMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.addUserAndDefaultRole({
      email,
      roleDomain,
      addDefaultLoginPermission,
      simulateRole: this.simulateRoleId,
    });
  };

  public addUserAndDefaultRoleWithRoleAndPermissions = async ({
    email,
    roleDomain,
  }: Omit<
    AddUserAndDefaultRoleWithRoleAndPermissionsMutationVariables,
    'simulateRole'
  >) => {
    return await this.graphQlRequestService.graphQlSdk.addUserAndDefaultRoleWithRoleAndPermissions(
      {
        email,
        roleDomain,
        simulateRole: this.simulateRoleId,
      }
    );
  };

  public changeRoleName = async ({
    id,
    newName,
  }: Omit<ChangeRoleNameMutationVariables, 'simulateRole'>) => {
    return await this.graphQlRequestService.graphQlSdk.changeRoleName({
      id,
      newName,
      simulateRole: this.simulateRoleId,
    });
  };

  public deleteRoleAndPermissions = async (
    variables: Omit<DeleteRoleAndPermissionsMutationVariables, 'simulateRole'>
  ) => {
    return await this.graphQlRequestService.graphQlSdk.deleteRoleAndPermissions(
      {
        ...variables,
        simulateRole: this.simulateRoleId,
      }
    );
  };

  public metaDataByUserRole = async (roleIds: string[]) => {
    return await this.graphQlRequestService.graphQlSdk.metaDataByUserRole({
      roleIds: roleIds,
      simulateRole: this.simulateRoleId,
    });
  };

  public listExecutorRolesForRole = async (
    variables: ListExecutorRolesForRoleQueryVariables,
    domain: string
  ) => {
    return await this.graphQlRequestService.graphQlSdk.listExecutorRolesForRole(
      {
        roleId: variables.roleId,
        domainFilter: domain,
        simulateRole: this.simulateRoleId,
      }
    );
  };

  public getMultiDomainAuthInfo = async () => {
    return await this.graphQlRequestService.graphQlSdk.getMultiDomainAuthInfo({
      simulateRole: this.simulateRoleId,
    });
  };
}
